import React, { useState } from "react";
import { Box, Button, Chip, Stack } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import { ReactionPicker } from "./ReactionPicker";
import { ReactionSummary } from "../../store/reactionStore";

interface ReactionBarProps {
  entityId: string;
  entityType: "activity" | "comment" | "photo";
  reactions: ReactionSummary[];
  userReaction: string | null;
  onToggleReaction: (reactionKey: string) => void;
  disabled?: boolean;
}

export const ReactionBar: React.FC<ReactionBarProps> = ({
  reactions,
  userReaction,
  onToggleReaction,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const pickerOpen = Boolean(anchorEl);

  const handleOpenPicker = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleSelectReaction = (reactionKey: string) => {
    onToggleReaction(reactionKey);
  };

  const handleClickReactionPill = (event: React.MouseEvent, reactionType: string) => {
    event.stopPropagation();
    // If it's the user's reaction, toggle it off
    // Otherwise open the picker to change
    if (userReaction === reactionType) {
      onToggleReaction(reactionType);
    } else {
      setAnchorEl(event.currentTarget as HTMLElement);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" data-testid="reaction-bar">
      {reactions.map((reaction) => (
        <Chip
          key={reaction.reaction_type}
          data-testid={`reaction-pill-${reaction.emoji}`}
          data-highlighted={userReaction === reaction.reaction_type}
          variant={userReaction === reaction.reaction_type ? "soft" : "outlined"}
          color={userReaction === reaction.reaction_type ? "primary" : "neutral"}
          onClick={(e) => handleClickReactionPill(e, reaction.reaction_type)}
          disabled={disabled}
          aria-label={
            userReaction === reaction.reaction_type
              ? `Remove ${reaction.emoji} reaction`
              : `View ${reaction.emoji} reactions`
          }
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor:
                userReaction === reaction.reaction_type ? "primary.200" : "background.level1",
            },
          }}
        >
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span>{reaction.emoji}</span>
            <span>{reaction.count}</span>
          </Box>
        </Chip>
      ))}

      <Button
        data-testid="add-reaction-button"
        variant="plain"
        color="neutral"
        size="sm"
        startDecorator={<AddIcon />}
        onClick={handleOpenPicker}
        disabled={disabled}
        aria-label="Add reaction"
        sx={{
          minHeight: 32,
          "&:hover": {
            backgroundColor: "background.level1",
          },
        }}
      >
        {reactions.length === 0 ? "Add reaction" : ""}
      </Button>

      <ReactionPicker
        open={pickerOpen}
        anchorEl={anchorEl}
        currentReaction={userReaction}
        onSelect={handleSelectReaction}
        onClose={handleClosePicker}
      />
    </Stack>
  );
};
